import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { MESSAGES } from '../../utils/constants';
import MFASetup from './MFASetup';
import { isPasswordValid } from '../../utils/passwordValidation';
import PasswordStrengthIndicator from '../common/PasswordStrengthIndicator';

// TOTP Input Component with 6 individual digit inputs
const TOTPInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([]);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);

  useEffect(() => {
    const digitsArray = value.split('').slice(0, 6);
    while (digitsArray.length < 6) {
      digitsArray.push('');
    }
    setDigits(digitsArray);
  }, [value]);

  const handleChange = (index, newValue) => {
    if (newValue.length > 1) {
      newValue = newValue.slice(-1);
    }

    if (!/^\d*$/.test(newValue)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = newValue;
    setDigits(newDigits);

    const totpValue = newDigits.join('');
    onChange(totpValue);

    if (newValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedDigits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (pastedDigits.length > 0) {
      const newDigits = [...digits];
      for (let i = 0; i < pastedDigits.length && i < 6; i++) {
        newDigits[i] = pastedDigits[i];
      }
      setDigits(newDigits);
      onChange(pastedDigits);

      const nextIndex = Math.min(pastedDigits.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
      {digits.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: 600,
              fontFamily: 'Metropolis, monospace',
              color: '#253E66',
              padding: '12px 0',
            }
          }}
          sx={{
            width: 48,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              borderRadius: '6px',
              '& fieldset': {
                borderColor: '#E5E5E5',
                borderWidth: 1,
              },
              '&:hover fieldset': {
                borderColor: '#D9DBDC',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#4698D3',
                borderWidth: 2,
              },
              '&.Mui-disabled fieldset': {
                borderColor: '#E5E5E5',
              },
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#BDBDBD',
            },
          }}
        />
      ))}
    </Box>
  );
};

const LoginPage = ({ onLogin, onAuthStatusChange }) => {
  const [formData, setFormData] = useState({
    username: 'admin',
    password: '',
    totpCode: ''
  });
  const [initFormData, setInitFormData] = useState({
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [authStatus, setAuthStatus] = useState(null);
  const [showInitForm, setShowInitForm] = useState(false);
  const [loginStep, setLoginStep] = useState('password');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await apiService.checkAuthStatus();
      if (response.success) {
        setAuthStatus(response.data);
        setShowInitForm(!response.data.initialized);
        if (onAuthStatusChange) {
          onAuthStatusChange(response.data);
        }
      }
    } catch (err) {
      console.error('Auth status check failed:', err);
      setAuthStatus({ initialized: false });
      setShowInitForm(true);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiService.login(
        formData.username,
        formData.password,
        formData.totpCode
      );

      if (response.success) {
        if (response.data.requiresMfaSetup) {
          setMfaSetupData(response.data);
          setLoginStep('setup');
          setMfaRequired(false);
          toast.info('MFA setup is required to continue');
        } else if (response.data.token) {
          const adminDetails = {
            username: response.data.username,
            role: response.data.role,
            isAdmin: response.data.isAdmin,
            customerId: response.data.customerId || null
          };
          localStorage.setItem('adminDetails', JSON.stringify(adminDetails));

          toast.success('Login successful');

          if (response.data.role === 'admin') {
            checkAndInitializeKeycloak();
          }

          setTimeout(() => {
            if (onLogin) {
              onLogin(response.data);
            }
          }, 1000);
        } else if (response.data.requiresMFA && !response.data.requiresMfaSetup) {
          setMfaRequired(true);
          setLoginStep('mfa');
          toast.success('Please enter your MFA code');
        } else {
          toast.error('Unexpected response from server');
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || MESSAGES.LOGIN_FAILED;
      toast.error(errorMessage);
      setMfaRequired(false);
      setLoginStep('password');
      setFormData(prev => ({ ...prev, totpCode: '' }));
    } finally {
      setLoading(false);
    }
  };

  const handleInitAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiService.initializeAdmin(initFormData.password);

      if (response.success) {
        toast.success('Admin initialized successfully');
        setInitFormData({ password: '' });

        if (response.data.requiresMFA) {
          setMfaSetupData(response.data);
          setLoginStep('setup');
          setShowInitForm(false);
        } else {
          await checkAuthStatus();
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Initialization failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const checkAndInitializeKeycloak = async () => {
    try {
      const statusResponse = await apiService.getKeycloakStatus();

      if (statusResponse.success && statusResponse.data.initialized) {
        return;
      }

      await apiService.initializeKeycloak();
    } catch (keycloakError) {
      console.error('Keycloak initialization failed:', keycloakError);
    }
  };

  const handleMFASetupComplete = async () => {
    toast.success('MFA setup completed. You can now login.');
    setMfaSetupData(null);
    setLoginStep('password');
    await checkAuthStatus();
  };

  const handleInputChange = (e, isInit = false) => {
    const { name, value } = e.target;
    if (isInit) {
      setInitFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const resetToPasswordStep = () => {
    setLoginStep('password');
    setMfaRequired(false);
    setFormData(prev => ({ ...prev, totpCode: '' }));
  };

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#F9FBFD',
          position: 'relative',
          px: 3,
          py: 4,
        }}
      >
        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
        <Box
          sx={{
            width: '100%',
            maxWidth: 420,
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
            p: 5,
          }}
        >
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src="/ck-auth-logo.png"
              alt="CloudKeeper"
              style={{
                height: 48,
                objectFit: 'contain',
              }}
            />
          </Box>

          {/* MFA Setup Flow */}
          {loginStep === 'setup' && mfaSetupData && (
            <MFASetup
              setupData={mfaSetupData}
              onComplete={handleMFASetupComplete}
              onError={(error) => toast.error(error)}
            />
          )}

          {/* Admin Initialization */}
          {showInitForm && authStatus && !authStatus.initialized && loginStep === 'password' && (
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '1.25rem',
                  color: '#253E66',
                  textAlign: 'center',
                  mb: 0.5,
                }}
              >
                Initialize{' '}
                <span style={{ color: '#D64794', fontWeight: 600 }}>Admin</span>
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  color: '#999999',
                  fontSize: '0.875rem',
                  textAlign: 'center',
                  mb: 4,
                  fontWeight: 500,
                }}
              >
                Create the initial super admin account
              </Typography>

              <Box component="form" onSubmit={handleInitAdmin}>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.875rem',
                    color: '#253E66',
                    mb: 1,
                    fontWeight: 500,
                  }}
                >
                  Super Admin Password
                </Typography>
                <TextField
                  fullWidth
                  type="password"
                  name="password"
                  value={initFormData.password}
                  onChange={(e) => handleInputChange(e, true)}
                  placeholder="Enter a strong password"
                  required
                  disabled={loading}
                />
                <PasswordStrengthIndicator password={initFormData.password} />
                <Box sx={{ mb: 1.5 }} />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || !isPasswordValid(initFormData.password)}
                  sx={{
                    backgroundColor: '#4698D3',
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '6px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#3580B8',
                    },
                    '&:disabled': {
                      backgroundColor: '#E5E5E5',
                      color: '#BDBDBD',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Initialize'
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {/* Login Form */}
          {!showInitForm && loginStep !== 'setup' && (
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '1.25rem',
                  color: '#253E66',
                  textAlign: 'center',
                  mb: 0.5,
                }}
              >
                Login into CloudKeeper Prism for
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '1.25rem',
                  color: '#D64794',
                  textAlign: 'center',
                  fontWeight: 600,
                  mb: 4,
                }}
              >
                Admin
              </Typography>

              <Box component="form" onSubmit={handleLogin}>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.875rem',
                    color: '#253E66',
                    mb: 1,
                    fontWeight: 500,
                  }}
                >
                  Username or email
                </Typography>
                <TextField
                  fullWidth
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Username or email"
                  required
                  disabled={loading || mfaRequired}
                  InputProps={{ readOnly: true }}
                  sx={{ mb: 2.5 }}
                />

                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.875rem',
                    color: '#253E66',
                    mb: 1,
                    fontWeight: 500,
                  }}
                >
                  Password
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Password"
                  required
                  disabled={loading || mfaRequired}
                  sx={{ mb: 3 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={mfaRequired}
                          sx={{ color: '#999999' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* MFA Step */}
                {mfaRequired && loginStep === 'mfa' && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.875rem',
                        color: '#253E66',
                        mb: 2,
                        fontWeight: 500,
                        textAlign: 'center',
                      }}
                    >
                      Enter TOTP Code
                    </Typography>

                    <TOTPInput
                      value={formData.totpCode}
                      onChange={(value) => setFormData(prev => ({ ...prev, totpCode: value }))}
                      disabled={loading}
                    />

                    <Typography
                      sx={{
                        color: '#999999',
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.75rem',
                        textAlign: 'center',
                        mt: 2,
                        fontWeight: 500,
                      }}
                    >
                      Enter the 6-digit code from your authenticator app
                    </Typography>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading || (!formData.password || (mfaRequired && formData.totpCode.length !== 6))}
                    sx={{
                      backgroundColor: '#4698D3',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: '6px',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#3580B8',
                      },
                      '&:disabled': {
                        backgroundColor: '#E5E5E5',
                        color: '#BDBDBD',
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      mfaRequired ? 'Verify' : 'Login'
                    )}
                  </Button>

                  {mfaRequired && (
                    <Button
                      variant="contained"
                      onClick={resetToPasswordStep}
                      disabled={loading}
                      sx={{
                        backgroundColor: '#D64794',
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: '6px',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#B83A7D',
                        },
                      }}
                    >
                      Go Back
                    </Button>
                  )}
                </Box>
              </Box>


              {authStatus && !authStatus.initialized && (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Link
                    component="button"
                    onClick={() => setShowInitForm(true)}
                    underline="hover"
                    sx={{
                      color: '#4698D3',
                      fontWeight: 500,
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                    }}
                  >
                    Initialize Super Admin
                  </Link>
                </Box>
              )}
            </Box>
          )}
        </Box>
        </motion.div>

        {/* Footer */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 24,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'space-between',
            px: 4,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.8rem',
              color: '#999999',
              fontWeight: 500,
            }}
          >
            Have Questions?{' '}
            <Link
              href="mailto:support@cloudkeeper.com"
              underline="hover"
              sx={{ color: '#4698D3', fontWeight: 500 }}
            >
              Talk to our team
            </Link>
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.8rem',
              color: '#999999',
              fontWeight: 500,
            }}
          >
            CK Prism 2025 | All Rights Reserved
          </Typography>
        </Box>
      </Box>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default LoginPage;
